/**
 * Base URLs and the version prefix live here and nowhere else — never inlined
 * at a call site (`api-and-data-contracts.md` §1.3).
 *
 * Nine backend services behind the single nginx edge, every one on the
 * `/api/v1/…` prefix. The console tells them apart by base URL, not by path.
 * Eight of them render a surface; the ninth is read for its configuration
 * table alone (PRD-TDD §10.3).
 */

export const API_PREFIX = '/api/v1';

export const SERVICE_KEYS = [
  'auth',
  'content',
  'assets',
  'finance',
  'support',
  'engagement',
  'analytics',
  'notification',
  // The ninth service. It renders nothing in this console, but it **publishes**
  // a configuration table like every other deployed backend, and the runtime
  // configuration surface reads all nine — which is what the route map declares
  // and what the mockup draws. Recorded in PRD-TDD §10.3 as a nine-row table.
  'search',
] as const;

export type ServiceKey = (typeof SERVICE_KEYS)[number];

/**
 * The dev-only base-URL scheme. A production build must **throw** on it rather
 * than resolve it — guard 2 of `frontend-layout.md` §10.10. Without the throw
 * a mis-built bundle silently points at a scheme no browser can fetch and the
 * console fails as a timeout instead of as a build error.
 */
const DEV_SCHEME = 'mock:';

const RAW_BASE_URLS: Record<ServiceKey, string> = {
  auth: import.meta.env.VITE_AUTH_BASE_URL,
  content: import.meta.env.VITE_CONTENT_BASE_URL,
  assets: import.meta.env.VITE_ASSETS_BASE_URL,
  finance: import.meta.env.VITE_FINANCE_BASE_URL,
  support: import.meta.env.VITE_SUPPORT_BASE_URL,
  engagement: import.meta.env.VITE_ENGAGEMENT_BASE_URL,
  analytics: import.meta.env.VITE_ANALYTICS_BASE_URL,
  notification: import.meta.env.VITE_NOTIFICATION_BASE_URL,
  search: import.meta.env.VITE_SEARCH_BASE_URL,
};

export function baseUrlFor(service: ServiceKey): string {
  const raw = RAW_BASE_URLS[service];

  if (!raw) {
    throw new Error(`No base URL configured for "${service}". Set VITE_${service.toUpperCase()}_BASE_URL.`);
  }

  if (raw.startsWith(DEV_SCHEME)) {
    if (!import.meta.env.DEV) {
      throw new Error(
        `The dev-only "${DEV_SCHEME}" base URL reached a production build for "${service}". ` +
          'Point VITE_' +
          service.toUpperCase() +
          '_BASE_URL at a real origin.',
      );
    }
    // In dev the request is answered by the MSW worker, so the origin only has
    // to be a well-formed absolute URL — **but it must still tell the services
    // apart**. Production gives every service its own base URL behind one edge;
    // collapsing all of them onto the bare origin would make nine identical
    // requests indistinguishable to the worker, and a surface that fans out per
    // service would render the first service's answer nine times. The dev-only
    // segment restores exactly the distinction production already has, and it
    // never reaches a real edge: the branch above throws on this scheme in a
    // production build.
    return `${window.location.origin}/__mock/${service}`;
  }

  return raw.replace(/\/+$/, '');
}

export function endpoint(service: ServiceKey, path: string): string {
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrlFor(service)}${API_PREFIX}${suffix}`;
}

/**
 * The frontend-error channel.
 *
 * It used to be a bare path on this host, on the assumption that the edge
 * serves an API here. It does not: this host is a static SPA root with a
 * history fallback and no upstream, so every report `POST`ed to
 * `/api/v1/frontendErrors` came back **404** — and because a CSP violation is
 * itself an error, the console reported its own failures into a hole, twice
 * per page load.
 *
 * No backend owns this route on any service either (PRD-TDD §23), so there is
 * no correct path to point it at yet. The sibling subscriber app already
 * solved this the honest way: the target is a configured URL, and **unset
 * disables the handler entirely** rather than failing. This console now reads
 * the same switch, under the same name, so the two behave identically.
 */
export function frontendErrorsUrl(): string {
  // Read at CALL time, not at module load, so the value is a configuration
  // rather than a build-order accident — and so a test can exercise both the
  // configured and the unset branch without re-importing the module.
  return import.meta.env.VITE_FRONTEND_ERRORS_URL?.trim() ?? '';
}

export const BUILD_SHA = import.meta.env.VITE_BUILD_SHA;
export const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT;
